import {
  CapitalSocialSnapshot,
  ShareNumberingSnapshot,
  SocioSnapshot,
} from '../../operaciones-societarias-form/models/constitution-snapshot.model';
import { formatShareAmountEs } from './format-share-amount';
import { formatTituloLabel } from './titulo-label.util';

export interface SocioTituloRow {
  socioIndex: number;
  nombre: string;
  nif: string;
  sharesCountNonUsuf: number;
  totalAdjusted: number;
  totalVn: number;
  participationPercent: string;
  isPresident: boolean;
  isSecretary: boolean;
  isWorker: boolean;
  rowCount: number;
  titulo: string;
  tituloLabel: string;
  sharesCountAdjustedForTitulo: number;
  isFirst: boolean;
  isUsufructo: boolean;
  socio: SocioSnapshot;
}

export interface GroupedNumeracionRow {
  sharesNumberFrom: number;
  sharesNumberTo: number;
  sharesCount: number;
  titulo: string | undefined;
  ownershipPercentage: number | null;
  adjustedShares: number;
  vnEur: number | null;
  isUsufructo: boolean;
}

export interface GroupedShareSeries {
  groupId: string;
  seriesId: number | null;
  seriesName: string;
  seriesTotalShares: number;
  partnerSharesInSeries: number;
  nominalValue: number;
  rows: GroupedNumeracionRow[];
  subtotalAdjusted: number;
  subtotalVn: number;
  hasNonUsufructo: boolean;
}

export interface GroupedNumeracionRowModal extends GroupedNumeracionRow {
  globalIndex: number;
}

export interface GroupedShareSeriesModal extends GroupedShareSeries {
  rows: GroupedNumeracionRowModal[];
}

export interface LiveCapitalSerie {
  id: number;
  name: string;
  totalShares?: number | null;
  nominalValue?: number | null;
}

export interface SeriesParticipationTotal {
  seriesName: string;
  adjusted: number;
  denominator: number;
  fractionLabel: string;
  percentLabel: string;
}

function isUsufructoTitulo(titulo: string | undefined | null): boolean {
  return (titulo ?? '').trim().toLowerCase() === 'usufructo';
}

function normalizeSeriesKey(id: number | string | null | undefined): number | null {
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isNaN(n) ? null : n;
}

export function totalCapitalSocialEuro(capital: CapitalSocialSnapshot): number {
  if (capital.hasSeries) {
    return capital.series.reduce((acc, s) => acc + s.totalAcciones * s.valorNominal, 0);
  }
  return capital.totalAcciones * (capital.valorNominal ?? 0);
}

function resolveSeriesMetaByName(
  capital: CapitalSocialSnapshot,
  _seriesId: number | null,
  seriesName?: string,
): { name: string; totalShares: number; nominalValue: number } {
  if (!capital.hasSeries) {
    return {
      name: 'Serie única',
      totalShares: capital.totalAcciones,
      nominalValue: capital.valorNominal ?? 0,
    };
  }
  if (_seriesId != null) {
    const byId = capital.series.find(s => s.id != null && Number(s.id) === Number(_seriesId));
    if (byId) {
      return {
        name: byId.nombre,
        totalShares: byId.totalAcciones,
        nominalValue: Number(byId.valorNominal) || 0,
      };
    }
  }
  if (seriesName) {
    const byName = capital.series.find(
      s => s.nombre.localeCompare(seriesName, undefined, { sensitivity: 'base' }) === 0,
    );
    if (byName) {
      return {
        name: byName.nombre,
        totalShares: byName.totalAcciones,
        nominalValue: byName.valorNominal,
      };
    }
  }
  // Con una sola serie y una numeración sin nombre, es esa serie; si trae otro nombre, no se le presta el de la única.
  if (!seriesName && capital.series.length === 1) {
    const s = capital.series[0];
    return {
      name: s.nombre,
      totalShares: s.totalAcciones,
      nominalValue: s.valorNominal,
    };
  }
  return { name: seriesName || 'Serie', totalShares: 0, nominalValue: 0 };
}

export function partnerTotalAdjustedShares(
  numberings: ShareNumberingSnapshot[] | undefined | null,
  opts?: { includeUsufructo?: boolean },
): number {
  if (!numberings?.length) return 0;
  const includeUsufructo = opts?.includeUsufructo === true;
  const list = includeUsufructo
    ? numberings
    : numberings.filter(n => !isUsufructoTitulo(n.titulo));
  return list.reduce((acc, n) => {
    const pct = Number(n.ownershipPercentage ?? 0) / 100;
    return acc + (n.sharesCount ?? 0) * pct;
  }, 0);
}

export function partnerTotalVnEuro(
  capital: CapitalSocialSnapshot,
  shareNumberings: ShareNumberingSnapshot[] | undefined | null,
): number {
  if (!shareNumberings?.length) return 0;
  let sum = 0;
  for (const n of shareNumberings) {
    if (isUsufructoTitulo(n.titulo)) continue;
    const nominalValue = n.nominalValue ?? resolveSeriesMetaByName(capital, n.seriesId, n.seriesName).nominalValue;
    const sharesCount = n.sharesCount ?? 0;
    const pct = Number(n.ownershipPercentage ?? 0) / 100;
    sum += sharesCount * pct * nominalValue;
  }
  return sum;
}

export function buildSociosTituloRows(
  socios: SocioSnapshot[],
  capital: CapitalSocialSnapshot,
): SocioTituloRow[] {
  const totalCapitalEuro = totalCapitalSocialEuro(capital);
  const result: SocioTituloRow[] = [];

  socios.forEach((s, socioIndex) => {
    const numberings = s.shareNumberings ?? [];
    const tituloOrder: string[] = [];
    const tituloMap = new Map<string, { sharesCount: number; adjusted: number }>();

    numberings.forEach(n => {
      const key = (n.titulo ?? '').trim().toLowerCase() || '__none__';
      if (!tituloMap.has(key)) {
        tituloMap.set(key, { sharesCount: 0, adjusted: 0 });
        tituloOrder.push(key);
      }
      const entry = tituloMap.get(key)!;
      const pct = Number(n.ownershipPercentage ?? 0) / 100;
      entry.sharesCount += n.sharesCount ?? 0;
      entry.adjusted += (n.sharesCount ?? 0) * pct;
    });

    if (tituloOrder.length === 0) tituloOrder.push('__none__');
    if (!tituloMap.has('__none__')) tituloMap.set('__none__', { sharesCount: 0, adjusted: 0 });

    const sharesCountNonUsuf = numberings
      .filter(n => !isUsufructoTitulo(n.titulo))
      .reduce((acc, n) => acc + (n.sharesCount ?? 0), 0);

    const totalAdjusted = partnerTotalAdjustedShares(numberings);
    const totalVn = partnerTotalVnEuro(capital, numberings);
    const participationPercent =
      totalCapitalEuro > 0 ? formatPct4((totalVn / totalCapitalEuro) * 100) : '-';

    const rowCount = tituloOrder.length;

    tituloOrder.forEach((key, i) => {
      const entry = tituloMap.get(key)!;
      const isUsufructo = isUsufructoTitulo(key === '__none__' ? '' : key);
      result.push({
        socioIndex,
        nombre: s.nombre,
        nif: s.nif,
        sharesCountNonUsuf,
        totalAdjusted,
        totalVn,
        participationPercent,
        isPresident: !!s.esPresidente,
        isSecretary: !!s.esSecretario,
        isWorker: !!s.esSocioTrabajador,
        rowCount,
        titulo: key === '__none__' ? '' : key,
        tituloLabel: key === '__none__' ? '-' : formatTituloLabel(key),
        sharesCountAdjustedForTitulo: entry.adjusted,
        isFirst: i === 0,
        isUsufructo,
        socio: s,
      });
    });
  });

  return result;
}

function buildGroupedRowsFromIndices(
  shareNumberings: Array<{
    sharesCount?: number;
    sharesNumberFrom?: number;
    sharesNumberTo?: number;
    titulo?: string;
    ownershipPercentage?: number | null;
  }>,
  indices: number[],
  meta: { nominalValue: number },
  withGlobalIndex: boolean,
): GroupedNumeracionRow[] | GroupedNumeracionRowModal[] {
  return indices.map(globalIndex => {
    const n = shareNumberings[globalIndex];
    const isUsufructo = isUsufructoTitulo(n.titulo);
    const sharesCount = n.sharesCount ?? 0;
    const pct = Number(n.ownershipPercentage ?? 0) / 100;
    const adjustedShares = sharesCount * pct;
    const vnEur = isUsufructo ? null : adjustedShares * meta.nominalValue;
    const base = {
      sharesNumberFrom: n.sharesNumberFrom ?? 0,
      sharesNumberTo: n.sharesNumberTo ?? 0,
      sharesCount,
      titulo: n.titulo,
      ownershipPercentage: n.ownershipPercentage ?? null,
      adjustedShares,
      vnEur,
      isUsufructo,
    };
    return withGlobalIndex ? { ...base, globalIndex } : base;
  });
}

function assembleGroupedShareSeries(
  groupId: string,
  seriesId: number | null,
  meta: { name: string; totalShares: number; nominalValue: number },
  rows: GroupedNumeracionRow[],
): GroupedShareSeries {
  const subtotalAdjusted = rows.filter(r => !r.isUsufructo).reduce((s, r) => s + r.adjustedShares, 0);
  const subtotalVn = rows.filter(r => !r.isUsufructo).reduce((s, r) => s + (r.vnEur ?? 0), 0);
  const hasNonUsufructo = rows.some(r => !r.isUsufructo);
  const partnerSharesInSeries = rows.reduce((s, r) => s + r.sharesCount, 0);
  return {
    groupId,
    seriesId,
    seriesName: meta.name,
    seriesTotalShares: meta.totalShares,
    partnerSharesInSeries,
    nominalValue: meta.nominalValue,
    rows,
    subtotalAdjusted,
    subtotalVn,
    hasNonUsufructo,
  };
}

function resolveLiveSeriesMeta(
  capitalSeries: LiveCapitalSerie[],
  seriesId: number | null,
  seriesName?: string,
): { name: string; totalShares: number; nominalValue: number } {
  if (seriesId != null) {
    const byId = capitalSeries.find(s => s.id === seriesId);
    if (byId) {
      return {
        name: byId.name,
        totalShares: byId.totalShares ?? 0,
        nominalValue: Number(byId.nominalValue) || 0,
      };
    }
  }
  if (seriesName) {
    const byName = capitalSeries.find(
      s => s.name.localeCompare(seriesName, undefined, { sensitivity: 'base' }) === 0,
    );
    if (byName) {
      return {
        name: byName.name,
        totalShares: byName.totalShares ?? 0,
        nominalValue: Number(byName.nominalValue) || 0,
      };
    }
  }
  return { name: seriesName || 'Serie', totalShares: 0, nominalValue: 0 };
}

/** Agrupa numeraciones por seriesId (mismo patrón que readonly). */
export function buildGroupedShareSeriesForModal(
  shareNumberings: Array<{
    seriesId?: number | null;
    seriesName?: string;
    sharesCount?: number;
    sharesNumberFrom?: number;
    sharesNumberTo?: number;
    titulo?: string;
    ownershipPercentage?: number | null;
  }>,
  options: {
    hasSeries: boolean;
    capitalSeries: LiveCapitalSerie[];
    singleSeriesTotal?: number;
    singleSeriesNominal?: number;
  },
): GroupedShareSeriesModal[] {
  const { hasSeries, capitalSeries } = options;

  if (!hasSeries) {
    const meta = {
      name: 'Serie única',
      totalShares: options.singleSeriesTotal ?? 0,
      nominalValue: options.singleSeriesNominal ?? 0,
    };
    const indices = shareNumberings.map((_, i) => i);
    const rows = buildGroupedRowsFromIndices(shareNumberings, indices, meta, true) as GroupedNumeracionRowModal[];
    if (rows.length === 0) return [];
    return [assembleGroupedShareSeries('__single__', null, meta, rows) as GroupedShareSeriesModal];
  }

  const groupsMap = new Map<number, number[]>();
  const orphanIndices: number[] = [];
  const order: number[] = [];

  shareNumberings.forEach((n, idx) => {
    const sid = normalizeSeriesKey(n.seriesId);
    if (sid == null) {
      orphanIndices.push(idx);
      return;
    }
    if (!groupsMap.has(sid)) {
      groupsMap.set(sid, []);
      order.push(sid);
    }
    groupsMap.get(sid)!.push(idx);
  });

  const result: GroupedShareSeriesModal[] = [];

  for (const seriesId of order) {
    const indices = groupsMap.get(seriesId)!;
    const firstSn = shareNumberings[indices[0]];
    const meta = resolveLiveSeriesMeta(capitalSeries, seriesId, firstSn?.seriesName);
    const rows = buildGroupedRowsFromIndices(shareNumberings, indices, meta, true) as GroupedNumeracionRowModal[];
    result.push(assembleGroupedShareSeries(String(seriesId), seriesId, meta, rows) as GroupedShareSeriesModal);
  }

  if (orphanIndices.length > 0) {
    const meta = { name: 'Sin serie', totalShares: 0, nominalValue: 0 };
    const rows = buildGroupedRowsFromIndices(shareNumberings, orphanIndices, meta, true) as GroupedNumeracionRowModal[];
    result.push(assembleGroupedShareSeries('null', null, meta, rows) as GroupedShareSeriesModal);
  }

  return result.sort((a, b) =>
    a.seriesName.localeCompare(b.seriesName, undefined, { sensitivity: 'base' }),
  );
}

export function buildGroupedShareSeries(
  shareNumberings: ShareNumberingSnapshot[],
  capital: CapitalSocialSnapshot,
): GroupedShareSeries[] {
  const hasSeries = capital.hasSeries;
  const keyOf = (sid: number | null) => (sid === null ? 'null' : String(sid));
  const order: (number | null)[] = [];
  const groupsMap = new Map<string, number[]>();

  shareNumberings.forEach((n, idx) => {
    const sid = hasSeries ? normalizeSeriesKey(n.seriesId) : null;
    const k = hasSeries ? keyOf(sid) : '__single__';
    if (!groupsMap.has(k)) {
      groupsMap.set(k, []);
      if (hasSeries) order.push(sid);
    }
    groupsMap.get(k)!.push(idx);
  });

  if (!hasSeries && groupsMap.has('__single__')) {
    order.push(null);
  }

  const result: GroupedShareSeries[] = [];
  for (const seriesId of order) {
    const k = hasSeries ? keyOf(seriesId) : '__single__';
    const indices = groupsMap.get(k)!;
    const firstSn = shareNumberings[indices[0]];
    const capitalMeta = resolveSeriesMetaByName(capital, seriesId, firstSn?.seriesName);
    // El VN guardado con la numeración (serie amortizada que ya no está en el capital) manda sobre el del capital.
    const guardado = indices.map(i => shareNumberings[i].nominalValue).find(v => v != null);
    const meta = guardado != null ? { ...capitalMeta, nominalValue: guardado } : capitalMeta;
    const groupId = hasSeries ? (seriesId === null ? 'null' : String(seriesId)) : '__single__';

    const rows = buildGroupedRowsFromIndices(shareNumberings, indices, meta, false) as GroupedNumeracionRow[];
    result.push(assembleGroupedShareSeries(groupId, seriesId, meta, rows));
  }

  return result.sort((a, b) =>
    a.seriesName.localeCompare(b.seriesName, undefined, { sensitivity: 'base' }),
  );
}

export function buildSeriesParticipationTotals(
  groups: GroupedShareSeries[],
): SeriesParticipationTotal[] {
  return groups
    .filter(g => g.hasNonUsufructo)
    .map(g => {
      const denom = g.seriesTotalShares;
      return {
        seriesName: g.seriesName,
        adjusted: g.subtotalAdjusted,
        denominator: denom,
        fractionLabel:
          denom > 0
            ? `${formatShareAmountEs(g.subtotalAdjusted)} / ${formatShareAmountEs(denom)}`
            : '-',
        percentLabel: denom > 0 ? formatPct4((g.subtotalAdjusted / denom) * 100) : '-',
      };
    });
}

export function totalSociosShares(socios: SocioSnapshot[]): number {
  return socios.reduce((sum, s) => {
    const numberings = s.shareNumberings ?? [];
    if (numberings.length === 0) return sum + (s.numAcciones || 0);
    return sum + partnerTotalAdjustedShares(numberings);
  }, 0);
}

export function formatEuro(value: number | null | undefined): string {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return (
    n.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    }) + ' €'
  );
}

export function formatEuroInteger(value: number | null | undefined): string {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return (
    n.toLocaleString('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true,
    }) + ' €'
  );
}

export function formatPct4(value: number | null | undefined): string {
  if (value == null) return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return (
    n.toLocaleString('de-DE', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
      useGrouping: true,
    }) + ' %'
  );
}
